import { useEffect, useState } from 'react';
import { getCurrentUser, signInWithRedirect, signOut } from 'aws-amplify/auth';
import type { ResearchItem, ResearchRun, RunIndexEntry } from '@yancheng/contracts';
import { configureAuth } from './auth';
import { ApiError, getItem, getRun, listRuns } from './api';
import { loadRuntimeConfig, type RuntimeConfig } from './config';

type RunDetail = RunIndexEntry & { run: ResearchRun; delivery?: { status?: string; error?: string } };

export default function App() {
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [projectId, setProjectId] = useState('rednote');
  const [runs, setRuns] = useState<RunIndexEntry[]>([]);
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [item, setItem] = useState<ResearchItem | null>(null);
  const [status, setStatus] = useState('Loading configuration…');

  useEffect(() => {
    void loadRuntimeConfig().then(async (runtime) => {
      configureAuth(runtime);
      setConfig(runtime);
      setProjectId(runtime.projects[0]?.id ?? 'rednote');
      try {
        await getCurrentUser();
        setAuthenticated(true);
        setStatus('Ready');
      } catch {
        setStatus('Sign in to continue');
      }
    }).catch((error: Error) => setStatus(error.message));
  }, []);

  useEffect(() => {
    if (!config || !authenticated) return;
    setStatus('Loading runs…');
    setDetail(null);
    setItem(null);
    void listRuns(config, projectId).then((result) => {
      setRuns(result.runs);
      setStatus(`${result.runs.length} runs`);
    }).catch((error: Error) => {
      if (error instanceof ApiError && error.status === 403) {
        setAccessDenied(true);
        setAuthenticated(false);
        setStatus('This Google account is not authorized for YanchengAdmin.');
        return;
      }
      setStatus(error.message);
    });
  }, [config, authenticated, projectId]);

  const openRun = async (run: RunIndexEntry) => {
    if (!config) return;
    setStatus('Loading run…');
    try {
      setDetail(await getRun(config, projectId, run.runId) as unknown as RunDetail);
      setItem(null);
      setStatus('Ready');
    } catch (error) {
      setStatus((error as Error).message);
    }
  };

  const openItem = async (target: ResearchItem) => {
    if (!config) return;
    setStatus('Loading item…');
    try {
      setItem(await getItem(config, projectId, target.itemId) as unknown as ResearchItem);
      setStatus('Ready');
    } catch (error) {
      setStatus((error as Error).message);
    }
  };

  if (!config) return <main className="center"><p>{status}</p></main>;
  if (accessDenied) return (
    <main className="center login-card">
      <p className="eyebrow">PRIVATE RESEARCH</p>
      <h1>Access denied</h1>
      <p>{status}</p>
      <button onClick={() => void signOut()}>Sign out</button>
    </main>
  );
  if (!authenticated) return (
    <main className="center login-card">
      <p className="eyebrow">PRIVATE RESEARCH</p>
      <h1>YanchengAdmin</h1>
      <p>Daily research artifacts from Rednote, job searches, and investment analysis.</p>
      <button onClick={() => void signInWithRedirect({ provider: 'Google' })}>Sign in with Google</button>
      <small>{status}</small>
    </main>
  );

  return (
    <div className="shell">
      <aside>
        <p className="eyebrow">YANCHENGADMIN</p>
        <h1>Research</h1>
        <nav>{config.projects.map((project) => <button className={projectId === project.id ? 'active' : ''} key={project.id} onClick={() => setProjectId(project.id)}>{project.label}</button>)}</nav>
        <button className="secondary" onClick={() => void signOut()}>Sign out</button>
      </aside>
      <main>
        <header><div><p className="eyebrow">{projectId}</p><h2>Daily runs</h2></div><span className="status">{status}</span></header>
        <section className="run-grid">
          {runs.map((run) => (
            <button className="run-card" key={run.runId} onClick={() => void openRun(run)}>
              <span>{new Date(run.completedAt).toLocaleString()}</span>
              <strong>{run.title}</strong>
              <small>{run.itemCount} items · {run.highlightCount} highlights · Slack {run.slackStatus}</small>
            </button>
          ))}
          {runs.length === 0 && <p>No uploaded runs yet.</p>}
        </section>
        {detail && <section className="detail">
          <h2>{detail.run.title}</h2>
          <p>{detail.run.summary}</p>
          <p className="meta">Slack: {detail.delivery?.status ?? detail.slackStatus}</p>
          <div className="items">{detail.run.items.map((entry) => <button key={entry.itemId} onClick={() => void openItem(entry)}><strong>{entry.title}</strong><span>{entry.category ?? entry.artifactType}</span></button>)}</div>
        </section>}
        {item && <section className="detail item-detail"><p className="eyebrow">{item.artifactType}</p><h2>{item.title}</h2><p>{item.body ?? item.summary}</p>{item.sourceUrl && <a href={item.sourceUrl} target="_blank" rel="noreferrer">Open original source ↗</a>}<pre>{JSON.stringify(item.metadata, null, 2)}</pre></section>}
      </main>
    </div>
  );
}
